import { Info as InfoIcon, InfoOutlined as InfoOutlinedIcon } from 'components/icons';
import LazyTooltip from 'components/LazyTooltip';
import { Tooltip } from 'components/ui/tooltip';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { ComponentProps } from 'react';
import { FormattedMessage } from 'react-intl';

type ColorType = 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' | undefined;

const ICON_COLOR: Record<NonNullable<ColorType>, string> = {
    primary: 'text-primary',
    secondary: 'text-secondary',
    error: 'text-error',
    warning: 'text-warning',
    info: 'text-info',
    success: 'text-success',
};

/**
 * The (i) after a label. It is a button, so the tooltip is a Tab stop and opens on keyboard focus as on hover,
 * named after what it explains when `label` says so. `nested` draws it as a plain icon instead, for the few
 * places it sits inside another button. `small` is a field label's in the new design's read views: a 14px glyph in a
 * 16px button, so the label's 16px line keeps its height.
 */
const InfoTooltip = ({
    text,
    values,
    color,
    className,
    label,
    nested,
    small,
}: {
    text?: string | string[];
    values?: ComponentProps<typeof FormattedMessage>['values'];
    color?: ColorType;
    className?: string;
    label?: string;
    nested?: boolean;
    small?: boolean;
}) => {
    if (!text || text.length === 0) return <span />;

    const title = Array.isArray(text) ? (
        <span className="flex flex-col gap-2">
            {text.map((item) => (
                // Same renderer as the single-key branch below, so a multi-paragraph
                // tooltip interpolates `values` too; a paragraph without placeholders
                // simply ignores them.
                <span key={item}>
                    <FormattedMessage id={item} values={values} />
                </span>
            ))}
        </span>
    ) : (
        <FormattedMessage key={text} id={text} values={values} />
    );
    const icon = small ? (
        <InfoIcon className={cn('size-3.5', className)} />
    ) : (
        <InfoOutlinedIcon className={cn('h-5 w-auto', color ? ICON_COLOR[color] : 'text-grey-400', className)} />
    );
    if (nested) {
        return (
            <LazyTooltip contentClassName="w-fit text-center" title={title}>
                {icon}
            </LazyTooltip>
        );
    }
    return (
        <Tooltip content={title} delay={0} disableHoverablePopup side="top" className="w-fit text-center">
            <button
                type="button"
                aria-label={label ? t('info-tooltip.about', { label }) : t('info-tooltip.more-information')}
                className={cn(
                    'focus-visible:ring-ring/50 inline-flex cursor-help rounded-full outline-none focus-visible:ring-3',
                    small && 'text-muted-foreground hover:text-foreground size-4 shrink-0 items-center justify-center'
                )}
            >
                {icon}
            </button>
        </Tooltip>
    );
};

export default InfoTooltip;
